import _filter from "lodash/filter";
import _isEmpty from "lodash/isEmpty";
import _get from "lodash/get";
import { createSelector } from 'reselect';
import { enrollFailed, isRateLimited } from "./credit-score";
import { dateToAge } from "../utils/age-math";
export var getGlobalProfile = function getGlobalProfile(identity) {
  return _get(identity, 'profiles.global_profile');
};
export var getFirstName = function getFirstName(identity) {
  return _get(identity, 'profiles.global_profile.first_name', '');
};
export var getLegalFirstName = function getLegalFirstName(identity) {
  return _get(identity, 'profiles.global_profile.legal_first_name', '');
};
export var getLastName = function getLastName(identity) {
  return _get(identity, 'profiles.global_profile.last_name', '');
};
export var getEmail = function getEmail(identity) {
  return _get(identity, 'current.email');
};
export var getIdentityId = function getIdentityId(identity) {
  return _get(identity, 'current.id');
};
export var getPhoneNumber = function getPhoneNumber(identity) {
  return _get(identity, 'profiles.global_profile.phone_number');
};
export var getMailingAddress = function getMailingAddress(identity) {
  return _get(identity, 'profiles.global_profile.mailing_address');
};
export var getPreviousAddress = function getPreviousAddress(identity) {
  return _get(identity, 'profiles.global_profile.previous_address');
};
export var getTuCreditReportProfile = function getTuCreditReportProfile(identity) {
  return _get(identity, 'profiles.tu_credit_report_profile');
};
export var getTuAuthenticationStatusProfile = function getTuAuthenticationStatusProfile(identity) {
  return _get(identity, 'profiles.tu_authentication_status_profile');
};
export var getTuActivatedAt = function getTuActivatedAt(identity) {
  return _get(identity, 'profiles.tu_authentication_status_profile.activated_at');
};
/*
  The is_stale flag gets set whenever the user receives a credit alert or update,
  but their credit report hasn't been updated yet.

  For older existing profiles that haven't been pulled yet, is_stale
  might be null or undefined, return false in this case.
*/

export var getIsCreditReportStale = function getIsCreditReportStale(identity) {
  return !!_get(identity, 'profiles.tu_credit_report_profile.is_stale');
};
export var isActive = function isActive(identity) {
  return _get(identity, 'profiles.tu_authentication_status_profile.status') === 'ACTIVE';
};
export var isPending = function isPending(identity) {
  return _get(identity, 'profiles.tu_authentication_status_profile.status') === 'PENDING';
}; // authenticated is a weird state your account can get into if the request to TU times out
// but you have successfuly created a user

export var isAuthenticated = function isAuthenticated(identity) {
  return _get(identity, 'profiles.tu_authentication_status_profile.status') === 'AUTHENTICATED';
};
export var isDeactivated = function isDeactivated(identity) {
  return _get(identity, 'profiles.tu_authentication_status_profile.status') === 'DEACTIVATED';
};
/**
 * @param  {object} identity The state of the redux identity reducer.
 * @return {boolean} is the user blocked by Transunion.
 */

export var isBlocked = function isBlocked(identity) {
  return _get(identity, 'profiles.tu_authentication_status_profile.status') === 'BLOCKED';
};
/*
 * It's possible for the has_thin_file to remain set after the TU authentication status has changed to deactivated or inactive
 * so we explicitly check for the PENDING status when determining if a user is in a thin file state (the flag gets set by identity when the
 * status transitions to PENDING)
 */

export var isThinFile = function isThinFile(identity) {
  return isPending(identity) && _get(identity, 'profiles.tu_authentication_status_profile.has_thin_file') === true;
}; // Rate limit error: too many connection attempts with TU

export var isTransunionDuplicate = function isTransunionDuplicate(identity) {
  return isThinFile(identity) && _get(identity, 'profiles.tu_authentication_status_profile.thin_file_reason') === 'DUPLICATE';
};
export var isTransunionNoData = function isTransunionNoData(identity) {
  return isThinFile(identity) && _get(identity, 'profiles.tu_authentication_status_profile.thin_file_reason') === 'NO_DATA';
};
export var isTransunionFailVerification = function isTransunionFailVerification(identity) {
  return isThinFile(identity) && _get(identity, 'profiles.tu_authentication_status_profile.thin_file_reason') === 'FAIL_VERIFICATION';
};
export var isTransunionOtherThinFile = function isTransunionOtherThinFile(identity) {
  return isThinFile(identity) && _get(identity, 'profiles.tu_authentication_status_profile.thin_file_reason') === 'OTHER';
};
/*
 * It's possible for the is_deceased to remain set after the TU authentication status has changed to deactivated or inactive
 * so we explicitly check for the ACTIVE status when determining if a user is in a thin file state (the flag gets set by identity when the
 * status transitions to ACTIVE)
 */

export var isDeceased = function isDeceased(identity) {
  return isActive(identity) && _get(identity, 'profiles.tu_authentication_status_profile.is_deceased') === true;
};
/**
 * @param  {object} identity The state of the redux identity reducer.
 * @return {boolean} Does the user have any Transunion verification questions.
 */

export var hasQuestions = function hasQuestions(identity) {
  return !!_get(identity, 'profiles.tu_authentication_status_profile.questions');
};
/*
 * It's possible for the not_enough_data to remain set after the TU authentication status has changed to deactivated or inactive
 * so we explicitly check for the ACTIVE status when determining if a user is in a thin file state (the flag gets set by identity when the
 * status transitions to ACTIVE)
 */

export var notEnoughData = function notEnoughData(identity) {
  return isActive(identity) && _get(identity, 'profiles.tu_authentication_status_profile.not_enough_data') === true;
};
export var hasUserBeenFound = function hasUserBeenFound(identity) {
  return hasQuestions(identity) || isThinFile(identity) || isActive(identity) || isDeactivated(identity);
};
export var getTuCreditReportPulledAt = function getTuCreditReportPulledAt(identity) {
  return _get(identity, 'profiles.tu_credit_report_profile.report_pulled_at');
};
export var getCreditBuilderProfilePulledAt = function getCreditBuilderProfilePulledAt(identity) {
  return _get(identity, 'profiles.credit_builder_profile.current_recommendations.pulled_at');
};
export var getUserHasCreditBuilderProfile = function getUserHasCreditBuilderProfile(identity) {
  return !_isEmpty(_get(identity, 'profiles.credit_builder_profile'));
};
/**
 * The tu_credit_report_profile and credit_builder_profile pulled_at fields should
 * always be the same. If they're different, this indicates a stale credit builder profile.
 */

export var getIsCreditBuilderProfileStale = createSelector(getTuCreditReportPulledAt, getCreditBuilderProfilePulledAt, function (tuCreditReportPulledAt, creditBuilderProfilePulledAt) {
  return tuCreditReportPulledAt !== creditBuilderProfilePulledAt;
});
export var getMyNerdWalletGoals = function getMyNerdWalletGoals(identity) {
  return _get(identity, 'profiles.my_nerdwallet_profile.goals');
};
export var getMyNerdwalletOnboardingFlags = function getMyNerdwalletOnboardingFlags(identity) {
  return _get(identity, 'profiles.my_nerdwallet_profile.onboarding_flags') || {};
};
export var getCreditFactorChangesSeenAtTime = function getCreditFactorChangesSeenAtTime(identity) {
  return _get(identity, 'profiles.my_nerdwallet_profile.credit_factor_changes_seen_at');
};
export var getHideTransactionTipSeenAtTime = function getHideTransactionTipSeenAtTime(identity) {
  return _get(identity, 'profiles.my_nerdwallet_profile.hide_transaction_tip_seen_at');
};
export var getAccountAggCtaDismissedFlags = function getAccountAggCtaDismissedFlags(identity) {
  return _get(identity, 'profiles.my_nerdwallet_profile.account_agg_cta_dismissed');
};
export var getNetWorthHideAccountTipDismissedAt = function getNetWorthHideAccountTipDismissedAt(identity) {
  return _get(identity, 'profiles.my_nerdwallet_profile.net_worth_hide_account_tip_dismissed_at');
};
/** @deprecated Credit Builder v2 is a deprecated feature. */

export var getCreditBuilderRecommendationsV2 = function getCreditBuilderRecommendationsV2(identity) {
  return _get(identity, 'profiles.credit_builder_profile.current_recommendations.recs', []);
};
/** @deprecated Credit Builder v2 is a deprecated feature. */

export var getIsRepeatCreditBuilderUser = function getIsRepeatCreditBuilderUser(identity) {
  return _get(identity, 'profiles.credit_builder_profile.explicit_user_preferences.repeat_user', false);
};
export var getUserPreferredIncomeEstimateSource = function getUserPreferredIncomeEstimateSource(identity) {
  return _get(identity, 'profiles.financial_profile.user_preferred_income_source');
};
export var getIncome = function getIncome(identity) {
  return _get(identity, 'profiles.financial_profile.annual_income');
};
export var getMonthlyNetIncome = function getMonthlyNetIncome(identity) {
  return _get(identity, 'profiles.financial_profile.monthly_net_income');
};
export var getSpousalIncome = function getSpousalIncome(identity) {
  return _get(identity, 'profiles.financial_profile.current_spouses_annual_income');
};
export var getMaritalStatus = function getMaritalStatus(identity) {
  return _get(identity, 'profiles.global_profile.marital_status');
};
export var getIsMarried = function getIsMarried(identity) {
  return _get(identity, 'profiles.global_profile.marital_status') === 'married';
};
export var getDateOfBirth = function getDateOfBirth(identity) {
  return _get(identity, 'profiles.global_profile.date_of_birth');
};
export var getAge = function getAge(identity) {
  return dateToAge(_get(identity, 'profiles.global_profile.date_of_birth'));
};
export var getAnnualRothIRAContribution = function getAnnualRothIRAContribution(identity) {
  return _get(identity, 'profiles.investing_profile.annual_roth_ira_contribution');
};
export var getAnnualIRAContribution = function getAnnualIRAContribution(identity) {
  return _get(identity, 'profiles.investing_profile.annual_ira_contribution');
};
export var get401kContributionRate = function get401kContributionRate(identity) {
  return _get(identity, 'profiles.investing_profile.annual_401k_contribution_percentage');
};
export var get401kEmployerMatchRate = function get401kEmployerMatchRate(identity) {
  return _get(identity, 'profiles.investing_profile.company_401k_dollar_match');
};
export var get401kEmployerContributionRate = function get401kEmployerContributionRate(identity) {
  return _get(identity, 'profiles.investing_profile.company_401k_contribution_match');
};
export var getFinancialProfile = function getFinancialProfile(identity) {
  return _get(identity, 'profiles.financial_profile') || {};
};
export var getFinancialProfileAccounts = function getFinancialProfileAccounts(identity) {
  var financialProfile = getFinancialProfile(identity);
  return _get(financialProfile, 'accounts', []);
};
export var getFinancialProfileBankAccounts = function getFinancialProfileBankAccounts(identity) {
  var accounts = getFinancialProfileAccounts(identity);
  return _filter(accounts, function (a) {
    return a.type === 'SAVINGS' || a.type === 'CHECKING';
  });
};
export var getFinancialSavingTypes = function getFinancialSavingTypes(identity) {
  return _get(identity, 'profiles.financial_profile.saving_types');
};
export var getFinancialSpendingPattern = function getFinancialSpendingPattern(identity) {
  return _get(identity, 'profiles.financial_profile.current_spending_pattern');
};
export var getFinancialSavingPattern = function getFinancialSavingPattern(identity) {
  return _get(identity, 'profiles.financial_profile.current_saving_pattern');
};
export var getFinancialAmountOwed = function getFinancialAmountOwed(identity) {
  return _get(identity, 'profiles.financial_profile.amount_owed');
};
export var getFinancialAnnualIncome = function getFinancialAnnualIncome(identity) {
  return _get(identity, 'profiles.financial_profile.annual_income');
};
export var getFinancialDebtTypes = function getFinancialDebtTypes(identity) {
  return _get(identity, 'profiles.financial_profile.debt_types');
};
export var getFinancialSelfReportedCreditRange = function getFinancialSelfReportedCreditRange(identity) {
  return _get(identity, 'profiles.financial_profile.credit_score_range');
};
export var makeGetFinancialDebtTypes = function makeGetFinancialDebtTypes() {
  return createSelector([getFinancialDebtTypes], function (debtTypes) {
    return debtTypes || {};
  });
};
export var getOnboardingType = function getOnboardingType(identity) {
  return _get(identity, 'profiles.onboarding_profile.onboarding_type');
};
export var getOnboardingInterest = function getOnboardingInterest(identity) {
  return _get(identity, 'profiles.onboarding_profile.onboarding_interest');
};
export var isDeactivateTUCustomerRequestPending = function isDeactivateTUCustomerRequestPending(identity) {
  return _get(identity, 'requests.deactivateTUCustomer.hasPendingRequests');
};
export var getHomeAppState = function getHomeAppState(identity, appName) {
  return _get(identity, "profiles.home_consumer_profile.app_state.".concat(appName));
};
export var getMortgageProfile = function getMortgageProfile(identity) {
  return _get(identity, 'profiles.mortgage_profile');
};
export var isTransunionBlocked = function isTransunionBlocked(identity) {
  return enrollFailed(identity) || isBlocked(identity) || isDeceased(identity) || isRateLimited(identity) || isThinFile(identity) || notEnoughData(identity);
};